import type { ApplicationHost, GameData } from '../app/GameApplication';
import { CAR_CONFIGS } from '../data/cars';
import { TRACK_CONFIGS } from '../data/tracks';
import { getSortedRegistryIds, selectionState } from '../SelectionState';
import { createMenuFlowController, type MenuAsyncToken } from '../menuFlow';
import { UIManager } from '../../ui/UIManager';
import { ApiClient, type ScoreDto } from '../../api/client';
import { realtimeClient } from '../../realtime/instance';
import { ACTIVE_RULESET_ID } from '@cars/shared';

export class MenuController {
  private cleanup: (() => void) | null = null;
  private readonly host: ApplicationHost;
  constructor(host: ApplicationHost) {
    this.host = host;
  }

  create(data: GameData): void {
    const uiLayer = this.host.uiRoot;
    if (!uiLayer) {
      throw new Error('Missing #ui-layer element');
    }

    const ui = new UIManager(uiLayer);
    const apiClient = new ApiClient('/api');
    const currentSelection = selectionState.get();
    const scoreboardTrackIds = getSortedRegistryIds(TRACK_CONFIGS);
    const flow = createMenuFlowController({
      initialSelection: currentSelection,
      selectionState,
      trackIds: scoreboardTrackIds,
      carIds: getSortedRegistryIds(CAR_CONFIGS),
    });
    let username = data.username?.trim() ?? '';
    const scoreboardCache = new Map<number, ScoreDto[]>();
    const scoreboardErrors = new Map<number, string>();
    if (data.startupError) scoreboardErrors.set(flow.get().selectedTrackId, data.startupError);

    const renderCarScreen = (): void => {
      if (!flow.isActive()) return;
      flow.openCars();
      const state = flow.get();
      ui.showCarSelectionScreen({
        selectedTrackId: state.selectedTrackId,
        selectedCarId: state.selectedCarId,
        onCarSelect: (carId) => {
          flow.selectCar(carId);
          renderCarScreen();
        },
        onBackToScores: () => {
          void renderScoreboardScreen();
        },
        onStartRace: () => {
          const race = flow.startRace();
          ui.clear();
          this.host.startRace({
            carId: race.selectedCarId,
            trackId: race.selectedTrackId,
            username,
          });
        },
      });
    };

    const renderScoreboardScreen = async (): Promise<void> => {
      if (!flow.isActive()) return;
      flow.openScores();
      const state = flow.get();
      const trackId = state.selectedTrackId;
      const cachedRows = scoreboardCache.get(trackId);
      const errorText = scoreboardErrors.get(trackId);
      const isLoading = !cachedRows && !errorText;
      const requestToken: MenuAsyncToken = flow.captureAsync('scores', trackId);

      ui.showScoreboardScreen({
        tabs: scoreboardTrackIds.map((id) => ({ trackId: id, label: `Track ${id}` })),
        activeTrackId: trackId,
        rows: cachedRows ?? [],
        errorText,
        isLoading,
        onTabSelect: (nextTrackId) => {
          flow.selectTrack(nextTrackId);
          void renderScoreboardScreen();
        },
        onStart: () => {
          renderCarScreen();
        },
        onWatchReplay: (replayId) => {
          const replayToken = requestToken;
          void apiClient
            .getReplay(replayId)
            .then((replay) => {
              if (!flow.isCurrent(replayToken)) return;
              flow.openReplay();
              ui.clear();
              this.host.showReplay({ replay, username });
            })
            .catch((error: unknown) => {
              if (!flow.isCurrent(replayToken)) return;
              scoreboardErrors.set(
                trackId,
                error instanceof Error ? error.message : 'Unable to load this replay.',
              );
              void renderScoreboardScreen();
            });
        },
      });

      if (!isLoading) {
        return;
      }

      try {
        const rows = await apiClient.getScores(trackId);
        if (!flow.isCurrent(requestToken)) return;
        scoreboardCache.set(trackId, rows);
        scoreboardErrors.delete(trackId);
      } catch {
        if (!flow.isCurrent(requestToken)) return;
        scoreboardErrors.set(trackId, 'Unable to load scoreboard for this track right now.');
      }

      if (flow.isCurrent(requestToken)) void renderScoreboardScreen();
    };

    realtimeClient.subscribe(scoreboardTrackIds, ACTIVE_RULESET_ID);
    const removeRealtimeListener = realtimeClient.on((event) => {
      const records =
        event.type === 'record-updated'
          ? [event.record]
          : event.type === 'snapshot'
            ? event.records
            : [];
      for (const record of records) {
        if (record.rulesetId !== ACTIVE_RULESET_ID) continue;
        scoreboardCache.delete(record.trackId);
        if (
          flow.isActive() &&
          flow.get().view === 'scores' &&
          flow.get().selectedTrackId === record.trackId
        )
          void renderScoreboardScreen();
      }
    });

    if (data.showScoreboard && username) {
      void renderScoreboardScreen();
    } else {
      ui.showStartScreen({
        onLogin: async ({ username: loginUsername, password }) => {
          await apiClient.login(loginUsername, password);
          realtimeClient.authenticate();
          username = loginUsername;
          await renderScoreboardScreen();
        },
        onRegister: async ({ username: registerUsername, password }) => {
          await apiClient.register(registerUsername, password);
          realtimeClient.authenticate();
          username = registerUsername;
          await renderScoreboardScreen();
        },
      });
    }

    this.cleanup = () => {
      removeRealtimeListener();
      flow.shutdown();
      ui.clear();
    };
  }
  update(): void {}
  resize(): void {}
  dispose(): void {
    this.cleanup?.();
    this.cleanup = null;
  }
}
